import React, { useEffect, useState } from 'react';
import { MapPin, Plus, Pencil, Trash2 } from 'lucide-react';
import '../pages.css';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { LoadingState, EmptyState, ErrorState } from '../../components/ui/States';
import { useToast } from '../../components/ui/Toast';
import { useAuth, authHeaders } from '../../auth';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_URL}/api`;

const EMPTY_FORM = {
  plantId: '', name: '', location: '', description: '', active: true,
  latitude: '', longitude: '',
  installedPanelCount: '', panelRatedPowerWp: '', commissioningDate: '',
  panelManufacturer: '', panelModel: '',
  vocV: '', vmpV: '', iscA: '', impA: '', nominalEfficiencyPct: '',
  tiltDeg: '', azimuthDeg: '', systemLossPct: '', inverterEfficiencyPct: '', pmaxTempCoefficientPctPerC: '',
  gridEmissionFactorKgCO2ePerKwh: '', gridEmissionFactorSource: '', gridEmissionFactorYear: ''
};

export default function PlantsAdminPage() {
  const { token } = useAuth();
  const toast = useToast();
  const [plants, setPlants] = useState(null);
  const [error, setError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPlant, setEditingPlant] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const headers = { 'Content-Type': 'application/json', ...authHeaders(token) };

  const load = async () => {
    setError(false);
    try {
      // Uses the merged /api/plants view (telemetry-observed plant_ids UNION
      // admin-registered metadata), not /api/admin/plants (metadata only) -
      // an admin hitting /api/plants gets the full unfiltered list (see
      // filterPlantsForUser: admins bypass scoping), so this shows every
      // plant that exists either way, including ones with live telemetry
      // but no metadata yet (previously invisible on this page).
      const res = await fetch(`${API_BASE}/plants`, { headers });
      if (!res.ok) throw new Error('load failed');
      setPlants(await res.json());
    } catch {
      setPlants([]);
      setError(true);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingPlant(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (plant) => {
    setEditingPlant(plant);
    setForm({
      plantId: plant.plant_id,
      name: plant.name || '',
      location: plant.location || '',
      description: plant.description || '',
      active: plant.active !== false,
      latitude: plant.latitude ?? '',
      longitude: plant.longitude ?? '',
      installedPanelCount: plant.installed_panel_count ?? '',
      panelRatedPowerWp: plant.panel_rated_power_wp ?? '',
      commissioningDate: plant.commissioning_date ?? '',
      panelManufacturer: plant.panel_manufacturer ?? '',
      panelModel: plant.panel_model ?? '',
      vocV: plant.voc_v ?? '',
      vmpV: plant.vmp_v ?? '',
      iscA: plant.isc_a ?? '',
      impA: plant.imp_a ?? '',
      nominalEfficiencyPct: plant.nominal_efficiency_pct ?? '',
      tiltDeg: plant.tilt_deg ?? '',
      azimuthDeg: plant.azimuth_deg ?? '',
      systemLossPct: plant.system_loss_pct ?? '',
      inverterEfficiencyPct: plant.inverter_efficiency_pct ?? '',
      pmaxTempCoefficientPctPerC: plant.pmax_temp_coefficient_pct_per_c ?? '',
      gridEmissionFactorKgCO2ePerKwh: plant.grid_emission_factor_kg_co2e_per_kwh ?? '',
      gridEmissionFactorSource: plant.grid_emission_factor_source ?? '',
      gridEmissionFactorYear: plant.grid_emission_factor_year ?? ''
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingPlant(null); setForm(EMPTY_FORM); };

  // Empty string means "not touched / cleared" - sent as null so the
  // backend can distinguish "clear the coordinate" from "leave it as is"
  // (see coerceCoordinate in adminRoutes.js). A non-empty value is parsed
  // client-side too so an obviously invalid entry ("abc") is caught before
  // the request even goes out.
  const coordOrNull = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw; // let the backend reject a genuinely invalid value
  };
  // Same "empty means untouched/cleared" contract as coordOrNull - all the
  // installation/spec fields are optional, so a blank input must reach the
  // backend as null (clear it), never as an empty string or a fabricated 0.
  const numOrNull = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  };
  const textOrNull = (raw) => (typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null);

  const installationPayload = () => ({
    installedPanelCount: numOrNull(form.installedPanelCount),
    panelRatedPowerWp: numOrNull(form.panelRatedPowerWp),
    commissioningDate: textOrNull(form.commissioningDate),
    panelManufacturer: textOrNull(form.panelManufacturer),
    panelModel: textOrNull(form.panelModel),
    vocV: numOrNull(form.vocV),
    vmpV: numOrNull(form.vmpV),
    iscA: numOrNull(form.iscA),
    impA: numOrNull(form.impA),
    nominalEfficiencyPct: numOrNull(form.nominalEfficiencyPct),
    tiltDeg: numOrNull(form.tiltDeg),
    azimuthDeg: numOrNull(form.azimuthDeg),
    systemLossPct: numOrNull(form.systemLossPct),
    inverterEfficiencyPct: numOrNull(form.inverterEfficiencyPct),
    pmaxTempCoefficientPctPerC: numOrNull(form.pmaxTempCoefficientPctPerC),
    gridEmissionFactorKgCO2ePerKwh: numOrNull(form.gridEmissionFactorKgCO2ePerKwh),
    gridEmissionFactorSource: textOrNull(form.gridEmissionFactorSource),
    gridEmissionFactorYear: numOrNull(form.gridEmissionFactorYear)
  });

  const submitForm = async (e) => {
    e.preventDefault();
    const latitude = coordOrNull(form.latitude);
    const longitude = coordOrNull(form.longitude);
    // Both-or-neither, enforced client-side too for a faster error than a
    // round-trip - the backend enforces the same rule authoritatively.
    if ((latitude === null) !== (longitude === null)) {
      toast.error('Latitude et longitude doivent être renseignées ensemble');
      return;
    }
    try {
      if (editingPlant) {
        const res = await fetch(`${API_BASE}/admin/plants/${editingPlant.plant_id}`, {
          method: 'PUT', headers,
          body: JSON.stringify({ name: form.name, location: form.location, description: form.description, active: form.active, latitude, longitude, ...installationPayload() })
        });
        if (!res.ok) throw new Error((await res.json()).error || 'Erreur de mise à jour');
        toast.success('Centrale mise à jour');
      } else {
        const res = await fetch(`${API_BASE}/admin/plants`, {
          method: 'POST', headers,
          body: JSON.stringify({ ...form, latitude, longitude, ...installationPayload() })
        });
        if (!res.ok) throw new Error((await res.json()).error || 'Erreur de création');
        toast.success('Centrale créée');
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const previewCapacityKw = (form.installedPanelCount !== '' && form.panelRatedPowerWp !== ''
    && Number.isFinite(Number(form.installedPanelCount)) && Number.isFinite(Number(form.panelRatedPowerWp)))
    ? (Number(form.installedPanelCount) * Number(form.panelRatedPowerWp)) / 1000
    : null;

  const confirmDelete = async () => {
    const plantId = deleteTarget;
    setDeleteTarget(null);
    try {
      const res = await fetch(`${API_BASE}/admin/plants/${plantId}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json()).error || 'Erreur de suppression');
      toast.success('Centrale supprimée');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'Gestion des Centrales' }]} />
      <div className="page-header">
        <h1><MapPin size={22} /> Gestion des Centrales</h1>
        <button className="btn-primary" onClick={openCreate}><Plus size={16} style={{ verticalAlign: 'middle' }} /> Nouvelle Centrale</button>
      </div>

      {plants === null ? (
        <LoadingState label="Chargement des centrales..." />
      ) : error ? (
        <ErrorState label="Impossible de charger les centrales" />
      ) : plants.length === 0 ? (
        <EmptyState label="Aucune centrale enregistrée" />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr><th>ID</th><th>Nom</th><th>Localisation</th><th>Coordonnées</th><th>Installation</th><th>Télémétrie</th><th>Statut</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {plants.map(plant => (
                <tr key={plant.plant_id}>
                  <td>{plant.plant_id}</td>
                  <td>{plant.name || '—'}</td>
                  <td>{plant.location || '—'}</td>
                  <td>
                    {plant.forecast_available
                      ? <span title={`Élévation: ${plant.elevation ?? '?'} m · Fuseau: ${plant.timezone || '?'}`}>{plant.latitude.toFixed(4)}, {plant.longitude.toFixed(4)}</span>
                      : <span className="summary-sub">Non configurées</span>}
                  </td>
                  <td>
                    <div style={{ fontSize: '0.82rem' }}>
                      {plant.installed_capacity_kw !== null && <div>{plant.installed_capacity_kw.toFixed(2)} kWp</div>}
                      <div style={{ color: '#999' }}>
                        {plant.installed_panel_count !== null ? `${plant.installed_panel_count} installé(s)` : 'Non configuré'}
                        {' · '}{plant.detected_panel_count} détecté(s){' · '}{plant.online_panel_count} en ligne
                      </div>
                      {(plant.panel_manufacturer || plant.panel_model) && (
                        <div style={{ color: '#7db3ff' }}>{plant.panel_manufacturer} {plant.panel_model}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-dot status-dot-${plant.has_telemetry ? 'online' : 'offline'}`}></span>
                    {plant.has_telemetry ? 'En ligne' : 'Aucune donnée'}
                  </td>
                  <td>{plant.active === false ? 'Inactif' : 'Actif'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="icon-btn" title="Modifier" onClick={() => openEdit(plant)}><Pencil size={14} /></button>
                      <button className="icon-btn icon-btn-danger" title="Supprimer" onClick={() => setDeleteTarget(plant.plant_id)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="confirm-overlay" onClick={closeForm}>
          <div className="confirm-dialog" style={{ maxWidth: 560, textAlign: 'left', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3>{editingPlant ? 'Modifier la Centrale' : 'Nouvelle Centrale'}</h3>
            <form onSubmit={submitForm}>
              <h4 className="section-title" style={{ fontSize: '0.85rem', marginTop: 0 }}>Général</h4>
              <div className="form-group">
                <label>ID de la Centrale</label>
                <input type="text" value={form.plantId} onChange={e => setForm({ ...form, plantId: e.target.value })} required disabled={!!editingPlant} placeholder="ex: gabes" />
              </div>
              <div className="form-group">
                <label>Nom</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Localisation</label>
                <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} />
              </div>
              <div className="form-group">
                <label className="checkbox-item">
                  <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} /> Actif
                </label>
              </div>

              <h4 className="section-title" style={{ fontSize: '0.85rem' }}>Localisation (prévisions météo)</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div className="form-group" style={{ flex: 1, minWidth: 140 }}>
                  <label>Latitude</label>
                  <input type="number" step="any" min="-90" max="90" value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })} placeholder="ex: 33.860162" />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 140 }}>
                  <label>Longitude</label>
                  <input type="number" step="any" min="-180" max="180" value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })} placeholder="ex: 10.075608" />
                </div>
              </div>
              <div className="summary-sub" style={{ marginBottom: 14, marginTop: -6 }}>
                Requises pour les prévisions météo (Open-Meteo). Laisser vide pour désactiver les prévisions de cette centrale.
                {editingPlant?.forecast_available && (
                  <> Élévation et fuseau horaire résolus automatiquement : {editingPlant.elevation ?? '?'} m, {editingPlant.timezone || '?'}.</>
                )}
              </div>

              <h4 className="section-title" style={{ fontSize: '0.85rem' }}>Installation</h4>
              <div className="form-group">
                <label>Nombre de panneaux installés</label>
                <input type="number" step="1" min="0" value={form.installedPanelCount} onChange={e => setForm({ ...form, installedPanelCount: e.target.value })} placeholder="ex: 20" />
              </div>
              <div className="form-group">
                <label>Puissance installée (calculée)</label>
                <div className="summary-card" style={{ padding: '10px 14px' }}>
                  <strong>{previewCapacityKw !== null ? `${previewCapacityKw.toFixed(2)} kWp` : 'Non calculable (nombre de panneaux et puissance nominale requis)'}</strong>
                </div>
              </div>
              <div className="form-group">
                <label>Date de mise en service</label>
                <input type="date" value={form.commissioningDate} onChange={e => setForm({ ...form, commissioningDate: e.target.value })} />
              </div>

              {/* Rated power lives here, not in Installation - it's a
                  property of the module itself (like Voc/Vmp/Isc/Imp), not
                  of how many are installed. Installed capacity above still
                  derives from this exact field (panelRatedPowerWp) plus
                  installedPanelCount - nothing duplicated, only moved. */}
              <h4 className="section-title" style={{ fontSize: '0.85rem' }}>Panneau Photovoltaïque</h4>
              <div className="summary-sub" style={{ marginBottom: 10, marginTop: -4 }}>
                Spécifications du module installé, selon la fiche technique du fabricant. Toutes ces valeurs sont optionnelles - laissez vide si le datasheet n'est pas disponible.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Fabricant</label>
                  <input type="text" maxLength={200} value={form.panelManufacturer} onChange={e => setForm({ ...form, panelManufacturer: e.target.value })} placeholder="ex: Jinko Solar" />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Modèle</label>
                  <input type="text" maxLength={200} value={form.panelModel} onChange={e => setForm({ ...form, panelModel: e.target.value })} placeholder="ex: JKM550M-72HL4" />
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Puissance nominale (Wp)</label>
                  <input type="number" step="any" min="0" value={form.panelRatedPowerWp} onChange={e => setForm({ ...form, panelRatedPowerWp: e.target.value })} placeholder="ex: 550" />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Rendement nominal constructeur (%)</label>
                  <input type="number" step="any" min="0" max="100" value={form.nominalEfficiencyPct} onChange={e => setForm({ ...form, nominalEfficiencyPct: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Tension à vide — Voc (V)</label>
                  <input type="number" step="any" min="0" value={form.vocV} onChange={e => setForm({ ...form, vocV: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Tension au PPM — Vmp (V)</label>
                  <input type="number" step="any" min="0" value={form.vmpV} onChange={e => setForm({ ...form, vmpV: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Courant de court-circuit — Isc (A)</label>
                  <input type="number" step="any" min="0" value={form.iscA} onChange={e => setForm({ ...form, iscA: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Courant au PPM — Imp (A)</label>
                  <input type="number" step="any" min="0" value={form.impA} onChange={e => setForm({ ...form, impA: e.target.value })} />
                </div>
              </div>

              <h4 className="section-title" style={{ fontSize: '0.85rem' }}>Prévision de Production (paramètres du modèle)</h4>
              <div className="summary-sub" style={{ marginBottom: 10, marginTop: -4 }}>
                Requis pour activer la page "Prévision de Production" pour cette centrale. Toutes ces valeurs sont optionnelles
                tant que la prévision de production n'est pas nécessaire.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Inclinaison des panneaux (°)</label>
                  <input type="number" step="any" min="0" max="90" value={form.tiltDeg} onChange={e => setForm({ ...form, tiltDeg: e.target.value })} placeholder="ex: 30" />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Azimut / Orientation (°)</label>
                  <input type="number" step="any" min="-180" max="180" value={form.azimuthDeg} onChange={e => setForm({ ...form, azimuthDeg: e.target.value })} placeholder="ex: 0" />
                </div>
              </div>
              <div className="summary-sub" style={{ marginBottom: 10, marginTop: -6 }}>
                Convention Open-Meteo : 0° = Sud, -90° = Est, +90° = Ouest, ±180° = Nord.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Pertes système estimées (%)</label>
                  <input type="number" step="any" min="0" max="100" value={form.systemLossPct} onChange={e => setForm({ ...form, systemLossPct: e.target.value })} placeholder="ex: 5" />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                  <label>Rendement onduleur (%)</label>
                  <input type="number" step="any" min="0" max="100" value={form.inverterEfficiencyPct} onChange={e => setForm({ ...form, inverterEfficiencyPct: e.target.value })} placeholder="ex: 96" />
                </div>
              </div>
              <div className="summary-sub" style={{ marginBottom: 10, marginTop: -6 }}>
                Pertes système = câblage, discordance, salissure, autres pertes non liées à l'onduleur (le rendement
                onduleur est appliqué séparément, sans double comptage).
              </div>
              <div className="form-group">
                <label>Coefficient de température Pmax (%/°C)</label>
                <input type="number" step="any" min="-2" max="0" value={form.pmaxTempCoefficientPctPerC} onChange={e => setForm({ ...form, pmaxTempCoefficientPctPerC: e.target.value })} placeholder="ex: -0.35" />
              </div>
              <div className="summary-sub" style={{ marginBottom: 14, marginTop: -6 }}>
                Valeur de la fiche technique du panneau (toujours négative ou nulle, ex. -0.35 %/°C).
              </div>

              <h4 className="section-title" style={{ fontSize: '0.85rem' }}>Impact Environnemental</h4>
              <div className="summary-sub" style={{ marginBottom: 10, marginTop: -4 }}>
                Facteur d'émission du réseau électrique local, utilisé pour estimer le CO₂e évité par cette centrale.
                Optionnel - laissez vide si aucune valeur vérifiée n'est disponible pour cette centrale (l'impact
                environnemental restera indisponible plutôt que d'utiliser une valeur inventée).
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
                  <label>Facteur d'émission réseau (kg CO₂e/kWh)</label>
                  <input type="number" step="any" min="0" max="2" value={form.gridEmissionFactorKgCO2ePerKwh} onChange={e => setForm({ ...form, gridEmissionFactorKgCO2ePerKwh: e.target.value })} placeholder="ex: 0.42" />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 120 }}>
                  <label>Année du facteur</label>
                  <input type="number" step="1" min="1990" value={form.gridEmissionFactorYear} onChange={e => setForm({ ...form, gridEmissionFactorYear: e.target.value })} placeholder="ex: 2023" />
                </div>
              </div>
              <div className="form-group">
                <label>Source du facteur</label>
                <input type="text" maxLength={200} value={form.gridEmissionFactorSource} onChange={e => setForm({ ...form, gridEmissionFactorSource: e.target.value })} placeholder="ex: IEA, autorité nationale de l'énergie..." />
              </div>

              <div className="confirm-actions">
                <button type="button" className="btn-secondary" onClick={closeForm}>Annuler</button>
                <button type="submit" className="btn-primary">{editingPlant ? 'Mettre à jour' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Supprimer la centrale"
        message="Cela supprime uniquement les métadonnées de cette centrale (nom, localisation, description). L'historique InfluxDB n'est pas affecté. Confirmer ?"
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
